import React, { useMemo, useState } from 'react';
import Loader from '../../common/Loader'; // Import Loader
import { CreditCard, Banknote } from 'lucide-react';
import { FaUniversity } from 'react-icons/fa';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { recordTransaction, recordRecentActivity } from '../../../api/apiService';

const PaymentMethodScreen = ({
    user,
    selectedOption,
    amount,
    onTransactionComplete,
    selectedPayment,
    onSelectPayment,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showSelectAccountAmountPrompt, setShowSelectAccountAmountPrompt] = useState(false);

    const MIN_AMOUNT_USD = 50;

    const parsedAmount = useMemo(() => {
        const n = parseFloat(amount);
        return Number.isFinite(n) ? n : NaN;
    }, [amount]);

    const canSelectPayment = Boolean(
        selectedOption &&
        amount !== '' &&
        Number.isFinite(parsedAmount) &&
        parsedAmount >= MIN_AMOUNT_USD
    );

    const paymentMethods = useMemo(
        () => [
            // Credit card icon
            {
                id: 'paypal',
                name: 'Credit / Debit Card',
                icon: <CreditCard className="h-8 w-8 text-red-600" />,
            },
            // Bank icon
            {
                id: 'bank',
                name: 'Bank Transfer',
                icon: <FaUniversity className="h-8 w-8 text-red-600" />,
            },
            // Temporarily disabled until integrations/UI are ready:
            // { id: 'crypto', name: 'Crypto', icon: '₮' },
            // { id: 'apple', name: 'Apple Pay', icon: '' },
            // { id: 'google', name: 'Google Pay', icon: 'G' },
        ],
        []
    );

    const handleDeposit = async (paymentMethod) => {
        if (!selectedOption || !amount || !paymentMethod) {
            alert('Please fill all fields and select a payment method.');
            return;
        }

        setIsLoading(true);

        let toType = '';
        if (selectedOption.startsWith('A')) {
            toType = 'account';
        } else if (selectedOption.startsWith('W')) {
            toType = 'wallet';
        }

        const transactionData = {
            transaction_id: `TXN_${Date.now()}`,
            user_id: user.user_id,
            amount: parseFloat(amount),
            transaction_type: 'Deposit',
            from_type: '',
            from_id: '',
            status: 'success',
            to_type: toType,
            to_id: selectedOption,
            date: new Date().toISOString(),
        };

        try {
            const transactionResponse = await recordTransaction(user.access_token, transactionData);
            if (transactionResponse.error) {
                throw new Error('Failed to record transaction.');
            }

            const recentActivityData = {
                userId: user.user_id,
                description: `Your deposit transaction is successful. Transaction ID: ${transactionData.transaction_id}`,
                date: new Date().toISOString(),
                status: 'success',
            };

            const activityResponse = await recordRecentActivity(user.access_token, recentActivityData);
            if (activityResponse.error) {
                throw new Error('Failed to record recent activity.');
            }

            onTransactionComplete('success');
        } catch (error) {
            console.error('Error:', error);
            onTransactionComplete('failed');
        } finally {
            setIsLoading(false);
        }
    };

    const renderPaymentMethodUI = () => {
        switch (selectedPayment) {
            case 'paypal':
                return (
                    <div className="mt-4">
                        <PayPalScriptProvider options={{ 'client-id': process.env.REACT_APP_PAYPAL_CLIENT_ID }}>
                            <PayPalButtons
                                createOrder={(data, actions) => {
                                    return actions.order.create({
                                        purchase_units: [
                                            {
                                                amount: {
                                                    value: amount, // Amount to be paid
                                                },
                                            },
                                        ],
                                    });
                                }}
                                onApprove={(data, actions) => {
                                    return actions.order.capture().then(() => {
                                        handleDeposit('paypal');
                                    });
                                }}
                                onError={(error) => {
                                    console.error('PayPal Error:', error);
                                    onTransactionComplete('failed');
                                }}
                            />
                        </PayPalScriptProvider>
                    </div>
                );

            case 'crypto':
                return (
                    <div className="mt-4">
                        <p className="text-gray-600">Please send the amount to the following Tether (USDT) wallet address:</p>
                        <p className="text-gray-800 font-semibold">Wallet Address: 0x1234567890abcdef</p>
                    </div>
                );

            case 'bank':
                return (
                    <div className="mt-4">
                        <p className="text-gray-600">Please transfer the amount to the following bank account:</p>
                        <div className="text-gray-800 font-semibold">
                            <p><strong>Bank Name:</strong> MCB Mauritius</p>
                            <p><strong>Account Name:</strong> INVESTAiN Ltd</p>
                            <p><strong>IBAN:</strong> MU71MCBL0944000453343357000</p>
                            <p><strong>SWIFT/BIC Code:</strong> MCBLMUMU</p>
                            <p><strong>Reference:</strong> Your INVESTAiN Trading Account Number</p>
                            <p><strong>Purpose Code of the Payment:</strong> FIS - Financial Services</p>
                        </div>
                    </div>
                );

            case 'apple':
            case 'google':
                return (
                    <div className="mt-4">
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                            <p className="text-sm text-gray-700">
                                Payment UI is not configured for Apple Pay / Google Pay yet.
                                Please use Credit / Debit Card or Bank Transfer.
                            </p>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <>
            {isLoading && <Loader />}

            {!canSelectPayment && showSelectAccountAmountPrompt && (
                <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    Please select an account and enter an amount to enable payment methods.
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {paymentMethods.map((method) => {
                    const isSelected = method.id === selectedPayment;
                    const isDisabled = !canSelectPayment;

                    return (
                        <button
                            key={method.id}
                            type="button"
                            disabled={isDisabled}
                            className={[
                                'p-4 border rounded-xl transition-all duration-150 flex flex-col items-center justify-center gap-2',
                                isSelected
                                    ? 'border-red-500 bg-red-50 shadow-sm'
                                    : 'border-gray-200 bg-white hover:shadow-sm',
                                isDisabled ? 'opacity-50 cursor-not-allowed hover:shadow-none' : '',
                            ].join(' ')}
                            onClick={() => {
                                if (!canSelectPayment) {
                                    setShowSelectAccountAmountPrompt(true);
                                    return;
                                }
                                setShowSelectAccountAmountPrompt(false);
                                onSelectPayment?.(method.id);
                            }}
                        >
                            <div className="flex items-center justify-center">{method.icon}</div>
                            <span className="text-sm font-semibold text-gray-800">{method.name}</span>
                        </button>
                    );
                })}
            </div>

            {selectedPayment && renderPaymentMethodUI()}
        </>
    );
};

export default PaymentMethodScreen;
